import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon,
  TrashIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { exportAPI } from '../services/api';
import { useActivitiesStore, SavedActivity } from '../store/activitiesStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function to generate UUIDs
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

interface AdvisorCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
}

interface UrgencyLevel {
  id: string;
  name: string;
  description: string;
}

interface ConsultationResult {
  session_id: string;
  category: string;
  situation: string;
  advice: string;
  recommended_actions: string[];
  relevant_resources: string[];
  disclaimers: string[];
  follow_up_questions: string[];
  timestamp: Date;
}

// Countries list
const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
  'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Switzerland',
  'Austria', 'Sweden', 'Norway', 'Denmark', 'Finland', 'Ireland',
  'Portugal', 'Greece', 'Poland', 'Czech Republic', 'Hungary', 'Romania',
  'Japan', 'China', 'South Korea', 'India', 'Singapore', 'Malaysia',
  'Thailand', 'Indonesia', 'Philippines', 'Vietnam', 'Brazil', 'Mexico',
  'Argentina', 'Chile', 'Colombia', 'Peru', 'South Africa', 'Egypt',
  'Nigeria', 'Kenya', 'Israel', 'United Arab Emirates', 'Saudi Arabia',
  'Turkey', 'Russia', 'Ukraine', 'New Zealand',
].sort();

// States by country
const STATES_BY_COUNTRY: Record<string, string[]> = {
  'United States': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],
  'Canada': [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ],
  'Australia': [
    'Australian Capital Territory', 'New South Wales', 'Northern Territory', 'Queensland',
    'South Australia', 'Tasmania', 'Victoria', 'Western Australia'
  ],
  'United Kingdom': ['England', 'Scotland', 'Wales', 'Northern Ireland'],
  'Germany': [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse',
    'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate',
    'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'
  ],
};

export const AIAdvisor: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Form state
  const [category, setCategory] = useState('');
  const [situation, setSituation] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [urgency, setUrgency] = useState('');
  const [budgetConcern, setBudgetConcern] = useState(false);
  const [specificQuestions, setSpecificQuestions] = useState<string[]>([]);
  const [newQuestion, setNewQuestion] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<AdvisorCategory[]>([]);
  const [urgencyLevels, setUrgencyLevels] = useState<UrgencyLevel[]>([]);
  const [consultationHistory, setConsultationHistory] = useState<ConsultationResult[]>([]);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  // Document upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [documentQuestion, setDocumentQuestion] = useState('');

  // Activity management
  const { addActivity, updateActivity, currentActivityId, setCurrentActivityId, getActivityById } = useActivitiesStore();
  const [activityName, setActivityName] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load categories and urgency levels on mount
  useEffect(() => {
    fetchCategories();
    fetchUrgencyLevels();
  }, []);

  // Clear state when country changes
  useEffect(() => {
    setState('');
  }, [country]);

  // Track unsaved changes
  useEffect(() => {
    if (consultationHistory.length > 0) {
      setHasUnsavedChanges(true);
      setIsSaved(false);
    }
  }, [consultationHistory.length]);

  // Load activity if navigated from sidebar
  useEffect(() => {
    const navState = location.state as { loadActivityId?: string; clearActivity?: boolean } | null;
    if (navState?.clearActivity) {
      resetForm();
    } else if (navState?.loadActivityId) {
      const activity = getActivityById(navState.loadActivityId);
      if (activity && activity.activityType === 'ai-advisor') {
        setActivityName(activity.name);
        setConsultationHistory(activity.actionHistory as unknown as ConsultationResult[]);
        setCurrentActivityId(activity.id);
        setIsSaved(true);
        setHasUnsavedChanges(false);
      }
    }
  }, [location.state, getActivityById, setCurrentActivityId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [consultationHistory]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-advisor/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchUrgencyLevels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ai-advisor/urgency-levels`);
      if (response.ok) {
        const data = await response.json();
        setUrgencyLevels(data.levels);
      }
    } catch (error) {
      console.error('Failed to fetch urgency levels:', error);
    }
  };

  const resetForm = () => {
    setCategory('');
    setSituation('');
    setCountry('');
    setState('');
    setMunicipality('');
    setUrgency('');
    setBudgetConcern(false);
    setSpecificQuestions([]);
    setNewQuestion('');
    setConsultationHistory([]);
    setActiveSessionId(null);
    setUploadedFile(null);
    setDocumentQuestion('');
    setActivityName('');
    setCurrentActivityId(null);
    setIsSaved(false);
    setHasUnsavedChanges(false);
  };

  const addSpecificQuestion = () => {
    if (newQuestion.trim()) {
      setSpecificQuestions([...specificQuestions, newQuestion.trim()]);
      setNewQuestion('');
    }
  };

  const removeSpecificQuestion = (index: number) => {
    setSpecificQuestions(specificQuestions.filter((_, i) => i !== index));
  };

  const handleConsult = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!category || !situation.trim()) {
      alert(t('aiAdvisor:errors.categoryRequired'));
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/ai-advisor/consult`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          category,
          situation,
          country: country || null,
          state: state || null,
          municipality: municipality || null,
          urgency: urgency || null,
          budget_concern: budgetConcern,
          specific_questions: specificQuestions.length > 0 ? specificQuestions : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get advice');
      }

      const data = await response.json();

      const result: ConsultationResult = {
        session_id: data.session_id,
        category,
        situation,
        advice: data.advice,
        recommended_actions: data.recommended_actions,
        relevant_resources: data.relevant_resources,
        disclaimers: data.disclaimers,
        follow_up_questions: data.follow_up_questions,
        timestamp: new Date(),
      };

      const updatedHistory = [...consultationHistory, result];
      setConsultationHistory(updatedHistory);
      setActiveSessionId(data.session_id);

      // Auto-save activity
      await saveActivity(updatedHistory);

      // Clear form for next consultation
      setSituation('');
      setSpecificQuestions([]);

    } catch (error) {
      console.error('Consultation error:', error);
      alert(t('aiAdvisor:errors.consultFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async (sessionId: string) => {
    if (!followUpQuestion.trim()) return;

    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const lastConsultation = consultationHistory.find(c => c.session_id === sessionId);

      const response = await fetch(`${API_BASE_URL}/ai-advisor/follow-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          session_id: sessionId,
          question: followUpQuestion,
          context: lastConsultation?.advice.substring(0, 2000) || '',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get follow-up response');
      }

      const data = await response.json();

      // Add follow-up as a new consultation entry
      const result: ConsultationResult = {
        session_id: sessionId,
        category: lastConsultation?.category || 'follow-up',
        situation: `Follow-up: ${followUpQuestion}`,
        advice: data.response,
        recommended_actions: [],
        relevant_resources: [],
        disclaimers: [data.disclaimer],
        follow_up_questions: [],
        timestamp: new Date(),
      };

      const updatedHistory = [...consultationHistory, result];
      setConsultationHistory(updatedHistory);
      setFollowUpQuestion('');

      // Auto-save activity
      await saveActivity(updatedHistory);

    } catch (error) {
      console.error('Follow-up error:', error);
      alert(t('aiAdvisor:errors.followUpFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentAnalysis = async () => {
    if (!uploadedFile || !documentQuestion.trim() || !category) {
      alert(t('aiAdvisor:errors.documentRequired'));
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('category', category);
      formData.append('question', documentQuestion);
      if (country) formData.append('country', country);
      if (state) formData.append('state', state);

      const response = await fetch(`${API_BASE_URL}/ai-advisor/analyze-document`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to analyze document');
      }

      const data = await response.json();

      const result: ConsultationResult = {
        session_id: data.session_id,
        category,
        situation: `Document Analysis: ${uploadedFile.name}\n\nQuestion: ${documentQuestion}`,
        advice: data.analysis,
        recommended_actions: [],
        relevant_resources: [],
        disclaimers: [data.disclaimer],
        follow_up_questions: [],
        timestamp: new Date(),
      };

      const updatedHistory = [...consultationHistory, result];
      setConsultationHistory(updatedHistory);
      setActiveSessionId(data.session_id);
      setUploadedFile(null);
      setDocumentQuestion('');

      // Auto-save activity
      await saveActivity(updatedHistory);

    } catch (error) {
      console.error('Document analysis error:', error);
      alert(t('aiAdvisor:errors.documentAnalysisFailed'));
    } finally {
      setLoading(false);
    }
  };

  const saveActivity = async (history: ConsultationResult[]) => {
    let name = activityName;
    if (!name.trim() && history.length > 0) {
      // Generate name from first consultation
      const first = history[0];
      const categoryName = categories.find(c => c.id === first.category)?.name || first.category;
      name = `${categoryName} - ${new Date().toLocaleDateString()}`;
      setActivityName(name);
    }

    if (name.trim()) {
      const activity: SavedActivity = {
        id: currentActivityId || generateUUID(),
        name,
        uploadedFiles: [],
        selectedFiles: new Set(),
        actionHistory: history as any,
        savedAt: new Date(),
        activityType: 'ai-advisor',
      };

      if (currentActivityId) {
        updateActivity(activity);
      } else {
        addActivity(activity);
        setCurrentActivityId(activity.id);
      }

      setIsSaved(true);
      setHasUnsavedChanges(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      const validExtensions = ['.pdf', '.docx', '.txt'];
      const isValid = validTypes.includes(file.type) || validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));

      if (!isValid) {
        alert(t('aiAdvisor:errors.invalidFileType'));
        return;
      }
      setUploadedFile(file);
    }
  };

  const deleteConsultation = (index: number) => {
    const updatedHistory = consultationHistory.filter((_, i) => i !== index);
    setConsultationHistory(updatedHistory);
    if (currentActivityId) {
      saveActivity(updatedHistory);
    }
  };

  const getConsultationContent = (consultation: ConsultationResult) => {
    return `# AI Legal Advisor Consultation
Date: ${consultation.timestamp.toLocaleString()}
Category: ${categories.find(c => c.id === consultation.category)?.name || consultation.category}

## Situation
${consultation.situation}

## Advice
${consultation.advice}

${consultation.recommended_actions.length > 0 ? `## Recommended Actions
${consultation.recommended_actions.map((a, i) => `${i + 1}. ${a}`).join('\n')}` : ''}

${consultation.relevant_resources.length > 0 ? `## Relevant Resources
${consultation.relevant_resources.map(r => `- ${r}`).join('\n')}` : ''}

## Disclaimers
${consultation.disclaimers.map(d => `- ${d}`).join('\n')}
`;
  };

  const handleExportConsultation = (consultation: ConsultationResult) => async (format: 'word' | 'pdf' | 'text') => {
    const content = getConsultationContent(consultation);
    const categoryName = categories.find(c => c.id === consultation.category)?.name || consultation.category;

    try {
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `legal-consultation-${consultation.session_id.substring(0, 8)}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title: `AI Legal Advisor - ${categoryName}`,
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name !== 'AbortError') {
            console.warn('File System Access API failed, falling back to standard download:', err);
          } else {
            return;
          }
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const printConsultation = (consultation: ConsultationResult) => () => {
    const content = getConsultationContent(consultation);
    const categoryName = categories.find(c => c.id === consultation.category)?.name || consultation.category;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Convert markdown-like content to HTML
    const htmlContent = content
      .replace(/^# (.*?)$/gm, '<h1>$1</h1>')
      .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
      .replace(/^(\d+)\. (.*?)$/gm, '<li>$2</li>')
      .replace(/^- (.*?)$/gm, '<li>$1</li>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>AI Legal Advisor - ${categoryName}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; color: #1e40af; margin-bottom: 16px; }
            h2 { font-size: 18px; color: #1f2937; margin-top: 24px; margin-bottom: 12px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
            p { margin: 12px 0; }
            li { margin: 4px 0; margin-left: 20px; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <p>${htmlContent}</p>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Loading Overlay for AI processing */}
      <LoadingOverlay
        isVisible={loading}
        message={t('aiAdvisor:loading.processing', 'Processing...')}
        subMessage={t('aiAdvisor:loading.pleaseWait', 'Getting legal advice')}
      />

      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <LightBulbIcon className="h-12 w-12 text-yellow-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('aiAdvisor:title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('aiAdvisor:subtitle')}</p>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 max-w-7xl mx-auto w-full overflow-hidden">
        {/* Left Panel - Input Form */}
        <div className="lg:w-1/3 flex flex-col space-y-4 overflow-y-auto">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('aiAdvisor:form.category')} *
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('aiAdvisor:form.selectCategory')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Location Fields */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('aiAdvisor:form.country')}
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('aiAdvisor:form.selectCountry')}</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {country && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('aiAdvisor:form.state')}
                </label>
                {STATES_BY_COUNTRY[country] ? (
                  <select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('aiAdvisor:form.selectState')}</option>
                    {STATES_BY_COUNTRY[country].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder={t('aiAdvisor:form.enterState')}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('aiAdvisor:form.municipality')}
              </label>
              <input
                type="text"
                value={municipality}
                onChange={(e) => setMunicipality(e.target.value)}
                placeholder={t('aiAdvisor:form.enterMunicipality')}
                className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Urgency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('aiAdvisor:form.urgency')}
            </label>
            <select
              value={urgency}
              onChange={(e) => setUrgency(e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('aiAdvisor:form.selectUrgency')}</option>
              {urgencyLevels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.name}
                </option>
              ))}
            </select>
          </div>

          {/* Budget Concern */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="budgetConcern"
              checked={budgetConcern}
              onChange={(e) => setBudgetConcern(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="budgetConcern" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              {t('aiAdvisor:form.budgetConcern')}
            </label>
          </div>

          {/* Specific Questions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('aiAdvisor:form.specificQuestions')}
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecificQuestion())}
                placeholder={t('aiAdvisor:form.addQuestion')}
                className="flex-1 px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addSpecificQuestion}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('aiAdvisor:form.add')}
              </button>
            </div>
            {specificQuestions.length > 0 && (
              <ul className="space-y-1">
                {specificQuestions.map((q, i) => (
                  <li key={i} className="flex items-center justify-between text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded">
                    <span className="text-gray-700 dark:text-gray-300">{q}</span>
                    <button
                      onClick={() => removeSpecificQuestion(i)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Document Upload */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('aiAdvisor:form.uploadDocument')}
            </label>
            <div className="flex items-center gap-2">
              <label className="flex-1 cursor-pointer">
                <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 transition-colors">
                  <DocumentArrowUpIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {uploadedFile ? uploadedFile.name : t('aiAdvisor:form.selectFile')}
                  </span>
                </div>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
              {uploadedFile && (
                <button
                  onClick={() => setUploadedFile(null)}
                  className="p-2 text-red-500 hover:text-red-700"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            {uploadedFile && (
              <div className="mt-2">
                <input
                  type="text"
                  value={documentQuestion}
                  onChange={(e) => setDocumentQuestion(e.target.value)}
                  placeholder={t('aiAdvisor:form.documentQuestion')}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleDocumentAnalysis}
                  disabled={loading || !documentQuestion.trim()}
                  className="mt-2 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? t('aiAdvisor:form.analyzing') : t('aiAdvisor:form.analyzeDocument')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Consultation */}
        <div className="lg:w-2/3 flex flex-col bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden">
          {/* Situation Input */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <form onSubmit={handleConsult}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('aiAdvisor:form.situation')} *
              </label>
              <div className="relative">
                <textarea
                  value={situation}
                  onChange={(e) => setSituation(e.target.value)}
                  placeholder={t('aiAdvisor:form.situationPlaceholder')}
                  rows={4}
                  className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !category || !situation.trim()}
                  className="absolute bottom-3 right-3 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={t('aiAdvisor:form.getAdvice')}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
            </form>
          </div>

          {/* Consultation History */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {consultationHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <LightBulbIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>{t('aiAdvisor:noConsultations')}</p>
                <p className="text-sm mt-2">{t('aiAdvisor:getStarted')}</p>
              </div>
            ) : (
              consultationHistory.map((consultation, index) => (
                <div
                  key={`${consultation.session_id}-${index}`}
                  className="bg-white dark:bg-gray-700 rounded-lg shadow p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                        {categories.find(c => c.id === consultation.category)?.name || consultation.category}
                      </span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {consultation.timestamp.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <ExportDropdownMenu
                        onExport={handleExportConsultation(consultation)}
                        onPrint={printConsultation(consultation)}
                        showPrint={true}
                      />
                      <button
                        onClick={() => deleteConsultation(index)}
                        className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                        title={t('aiAdvisor:delete')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('aiAdvisor:situation')}:
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 text-sm italic">
                      {consultation.situation}
                    </p>
                  </div>

                  <div className="prose dark:prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {consultation.advice}
                    </ReactMarkdown>
                  </div>

                  {consultation.recommended_actions.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                        {t('aiAdvisor:recommendedActions')}
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {consultation.recommended_actions.map((action, i) => (
                          <li key={i} className="text-sm text-green-700 dark:text-green-300">
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {consultation.follow_up_questions.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                        {t('aiAdvisor:suggestedFollowUps')}
                      </h4>
                      <ul className="space-y-1">
                        {consultation.follow_up_questions.map((q, i) => (
                          <li
                            key={i}
                            className="text-sm text-blue-700 dark:text-blue-300 cursor-pointer hover:underline"
                            onClick={() => setFollowUpQuestion(q)}
                          >
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                    <p className="text-xs text-yellow-800 dark:text-yellow-200">
                      {consultation.disclaimers[0]}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Follow-up Input */}
          {activeSessionId && consultationHistory.length > 0 && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={followUpQuestion}
                  onChange={(e) => setFollowUpQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleFollowUp(activeSessionId)}
                  placeholder={t('aiAdvisor:form.followUpPlaceholder')}
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                <button
                  onClick={() => handleFollowUp(activeSessionId)}
                  disabled={loading || !followUpQuestion.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? '...' : t('aiAdvisor:form.askFollowUp')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-7xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('aiAdvisor:disclaimer.title')}:</strong> {t('aiAdvisor:disclaimer.text')}
        </p>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-900 dark:text-gray-100">{t('aiAdvisor:processing')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
